/**
 * @file: Composer.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * The discussion composer: a multiline note input, an add-media affordance
 * (backed by the shared lead uploader → {@link MediaComposeRow}), and a Send
 * button. Send is disabled when there is neither text nor an attachment, or
 * while any attachment is still uploading.
 */

import { Typography } from '@euler/app/components/typography';
import { useLeadMediaUploader } from '@euler/app/flows/leads/functions/useLeadMediaUploader';
import { MediaComposeRow } from '@euler/app/flows/leads/pages/detail/components/MediaComposeRow';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadMediaAttachmentInput } from '@euler/model/lead/attachment';
import { ImagePlus } from 'lucide-react-native';
import { useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import Toast from 'react-native-root-toast';

export const Composer = ({
  onSend,
}: {
  onSend: (
    body: string,
    attachments: LeadMediaAttachmentInput[],
  ) => Promise<void>;
}) => {
  const { rpx } = useResponsiveContext();
  const uploader = useLeadMediaUploader();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const hasAttachments = uploader.items.length > 0;
  const canSend =
    !sending &&
    !uploader.uploading &&
    !uploader.hasError &&
    (text.trim().length > 0 || uploader.collectUploaded().length > 0);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(text, uploader.collectUploaded());
      setText('');
      uploader.reset();
    } catch (e) {
      Toast.show((e as Error).message || '发送失败', {
        position: Toast.positions.BOTTOM,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={{ paddingTop: rpx(12) }}>
      <TextInput
        value={text}
        onChangeText={setText}
        multiline
        placeholder="输入内容..."
        placeholderTextColor={LD.mist}
        textAlignVertical="top"
        style={{
          backgroundColor: LD.canvas,
          borderRadius: rpx(12),
          minHeight: rpx(72),
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
          fontSize: rpx(14),
          lineHeight: rpx(20),
          color: LD.ink,
        }}
      />

      {hasAttachments ? (
        <View style={{ marginTop: rpx(10) }}>
          <MediaComposeRow
            items={uploader.items}
            onAdd={uploader.pick}
            onRemove={uploader.remove}
          />
        </View>
      ) : null}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: rpx(10),
        }}
      >
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={uploader.pick}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: rpx(6),
            paddingHorizontal: rpx(4),
          }}
        >
          <ImagePlus color={LD.steel} size={rpx(20)} />
          <Typography.Text
            size={rpx(13)}
            color={LD.steel}
            style={{ marginLeft: rpx(6) }}
          >
            照片 / 视频
          </Typography.Text>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.8}
          disabled={!canSend}
          onPress={send}
          style={{
            paddingVertical: rpx(8),
            paddingHorizontal: rpx(20),
            borderRadius: rpx(18),
            backgroundColor: canSend ? LD.brand : LD.hairline,
          }}
        >
          <Typography.Text
            size={rpx(14)}
            color={canSend ? '#fff' : LD.mist}
            medium
          >
            发送
          </Typography.Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};
